import React from 'react'
import {connect} from 'react-redux'
import styled from 'styled-components'
import {injectIntl} from 'react-intl'
import FormControl from '@mui/material/FormControl'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogTitle from '@mui/material/DialogTitle'
import Button from '@mui/material/Button'
import Select from '@mui/material/Select'
import InputLabel from '@mui/material/InputLabel'
import MenuItem from '@mui/material/MenuItem'
import TextField from '@mui/material/TextField'
import FormControlLabel from '@mui/material/FormControlLabel'
import Checkbox from '@mui/material/Checkbox'
import Tab from 'components/Tab'
import RollupDefinition from 'components/RollupDefinition'
import {createCard, updateCard, deleteCard} from 'modules/modules/actions'
import {fetchLinkedOptions} from 'modules/linkedfield/actions'
import {fieldtype} from 'utils/enums/fieldtype'
import {aggregatefunction, rollupFilterRules} from 'utils/enums/rollup'
import {chartType, sortDataType, barSize, barDirection, pieInnerRadius, pieLegendPosition} from 'utils/enums/graphics'
import theme from 'utils/enums/theme'
import {getLabelFields} from 'utils/utils'

const Tabs = styled.div`
    display: flex;
    margin-bottom: 30px;
`

const Container = styled.div`
    display: ${props => props.active ? 'block' : 'none'};
`

const Row = styled.div`
    display: flex;
    justify-content: space-between;
    margin-bottom: 20px;
`

const Control = styled(FormControl)`
	width: 45%;
`

const settings = {
    view: {
        code: 'view'
    },
    data: {
        code: 'data'
    }
}

class GraphicConfig extends React.Component {
    constructor(props) {
        super(props)

        const entity = props.card ? props.card.rollup.entityId : props.entities.length ? props.entities[0].id : null
        this.labelFields = entity ? getLabelFields(entity) : null
        this.fields = entity ? this.getFields(entity) : []

        this.state = {
            entity,
            label: props.card ? props.card.hideLabel : false,
            chart: props.card ? props.card.options.type : chartType.bar.code,
            color: props.card ? props.card.options.colorScheme : theme.palette.id,
            sort: props.card ? props.card.options.dataSort : sortDataType.valueasc,
            size: props.card && props.card.options.type === chartType.bar.code ? props.card.options.bar.width : barSize.Medium.id,
            direction: props.card && props.card.options.type === chartType.bar.code ? props.card.options.bar.direction : barDirection.vertical,
            showValues: props.card && props.card.options.type === chartType.bar.code ? props.card.options.bar.showValues : false,
            radius: props.card && props.card.options.type === chartType.pie.code ? props.card.options.pie.radius : pieInnerRadius.none.id,
            legend: props.card && props.card.options.type === chartType.pie.code ? props.card.options.pie.legend : pieLegendPosition.hide,
            labels: props.card && props.card.options.type === chartType.pie.code ? props.card.options.pie.showLabels : false,
            percents: props.card && props.card.options.type === chartType.pie.code ? props.card.options.pie.showPercents : false,
            name: props.card ? props.card.label : '',
            field: props.card ? props.card.rollup.fieldId : -1,
            calc: props.card ? props.card.rollup.calcType : aggregatefunction.Count,
            groupField: props.card ? props.card.rollup.groupField :
				this.labelFields && !!this.labelFields.length ? this.labelFields[0].id : null,
            filters: props.card ? this.props.card.rollup.filters : [],
            errors: [],
            settings: settings.view.code
        }
    }

    getFields = (entity) => {
        const table = this.props.entities.find(obj => obj.id === entity)
        if (table) {
            return table.fields.filter(item => item.uiType === fieldtype.number)
        } else {
            return []
        }
    }

    handleChange = (event) => {
        if (event.target.name === 'entity') {
            this.labelFields = getLabelFields(event.target.value)
            this.fields = this.getFields(event.target.value)

            this.setState((state) => ({
                field: state.calc === aggregatefunction.Count ? -1 : this.fields.length ? this.fields[0].id : null,
                entity: event.target.value,
                groupField: this.labelFields && !!this.labelFields.length ? this.labelFields[0].id : null,
                filters: []
            }))
        }

        if (event.target.name === 'groupField') {
            this.setState({
                groupField: event.target.value,
                filters: []
            })
        }

        if (event.target.name === 'calc' &&
            this.state.calc === aggregatefunction.Count &&
            event.target.value !== aggregatefunction.Count) {
            this.setState({
                field: this.fields.length ? this.fields[0].id : null
            })
        } else if (event.target.name === 'calc' &&
            event.target.value === aggregatefunction.Count) {
            this.setState({
                field: -1
            })
        }

        if (event.target.name !== 'entity' && event.target.name !== 'groupField') {
            this.setState({
                [event.target.name]: event.target.value
            })
        }
    }

    changeFilters = (filters) => {
        const list = filters.map(item => {
            return {
                fieldId: item.fieldId,
                rule: item.rule,
                valueMin: item.valueMin,
                valueMax: item.valueMax
            }
        })

        const errors = filters.filter(item => {
            const conditions = rollupFilterRules[item.fieldType]
                return (item.fieldType === fieldtype.number && item.rule === conditions.between && !item.valueMax) ||
                (item.fieldType === fieldtype.number &&  item.rule !== conditions.empty && !item.valueMin) ||
                (item.fieldType === fieldtype.text &&  item.rule !== conditions.empty && !item.valueMin) ||
                (item.fieldType === fieldtype.linkedfield && item.rule !== conditions.empty && !item.valueMin)
        })

        this.setState({
            filters: list,
            errors
        })
    }

    createCard = () => {
        const options = {
            type: this.state.chart,
            colorScheme: this.state.color,
            dataSort: this.state.sort
        }

        if (this.state.chart === chartType.bar.code) {
            options.bar = {
                width: this.state.size,
                direction: this.state.direction,
                showValues: this.state.showValues
            }
        } else {
            options.pie = {
                radius: this.state.radius,
                legend: this.state.legend,
                showLabels: this.state.labels,
                showPercents: this.state.percents
            }
        }

        this.props.createCard({
            label: this.state.name,
            moduleId: this.props.currentModule,
            hideLabel: this.state.label,
            rollup: {
                entityId: this.state.entity,
                calcType: this.state.calc,
                fieldId: this.state.field,
                groupField: this.state.groupField,
                filters: this.state.filters
            },
            options: options
        }, this.props.type)

        this.props.handleClose()
    }

    updateCard = () => {
        const options = {
            type: this.state.chart,
            colorScheme: this.state.color,
            dataSort: this.state.sort
        }

        if (this.state.chart === chartType.bar.code) {
            options.bar = {
                width: this.state.size,
                direction: this.state.direction,
                showValues: this.state.showValues
            }
        } else {
            options.pie = {
                radius: this.state.radius,
                legend: this.state.legend,
                showLabels: this.state.labels,
                showPercents: this.state.percents
            }
        }

        this.props.updateCard({
            id: this.props.cardId,
            label: this.state.name,
            moduleId: this.props.currentModule,
            hideLabel: this.state.label,
            rollup: {
                entityId: this.state.entity,
                calcType: this.state.calc,
                fieldId: this.state.field,
                groupField: this.state.groupField,
                filters: this.state.filters
            },
            options: options
        }, this.props.type)

        this.props.handleClose()
    }

    deleteCard = () => {
        this.props.deleteCard(this.props.cardId)
        this.props.handleClose()
    }

    render() {
        const {intl} = this.props

        return (
            <React.Fragment>
                <DialogTitle>
					{intl.formatMessage({id: this.props.cardId ? "main.edit_graphic" : "main.create_gph"})}
                </DialogTitle>
                <DialogContent>
                    <Tabs>
                        {Object.values(settings).map((item, index) =>
                            <Tab key={index} active={this.state.settings === item.code} onClick={() => this.setState({ settings: item.code })}>
                                {intl.formatMessage({id: `main.create_graphic_${item.code}`})}
                            </Tab>
                        )}
                    </Tabs>
                    <React.Fragment>
                        <Container active={this.state.settings === settings.view.code}>
                            <TextField variant="standard"
                                defaultValue={this.state.name}
                                onBlur={this.handleChange}
                                label={intl.formatMessage({id: "main.label_graphic_card"})}
                                sx={{
									width: '100%'
								}}
                                disabled={this.state.label}
                                InputLabelProps={{
                                    shrink: true
                                }}
                                inputProps={{
                                    maxLength: global.NAME_MAX_LENGTH,
                                    name: 'name'
                                }}
                            />
                            <FormControlLabel
								sx={{
									marginBottom: '20px'
								}}
                                control={
                                    <Checkbox
                                        checked={this.state.label}
                                        onChange={(e) => this.setState({ label: e.target.checked })}
                                    />
                                }
                                label={intl.formatMessage({id: "main.graphic_label_show"})}
                            />
                            <Row>
                                <Control variant="standard">
                                    <InputLabel shrink={true}>{intl.formatMessage({id: "main.graphic_chart"})}</InputLabel>
                                    <Select
                                        value={this.state.chart}
                                        onChange={this.handleChange}
                                        inputProps={{
                                            name: 'chart'
                                        }}
                                    >
                                        {Object.values(chartType).map((item, index) =>
                                            <MenuItem key={index} value={item.code}>
                                                {intl.formatMessage({id: `main.graphic_${item.label}`})}
                                            </MenuItem>
                                        )}
                                    </Select>
                                </Control>
                                <Control variant="standard">
                                    <InputLabel shrink={true}>{intl.formatMessage({id: "main.graphic_color"})}</InputLabel>
                                    <Select
                                        value={this.state.color}
                                        onChange={this.handleChange}
                                        inputProps={{
                                            name: 'color'
                                        }}
                                    >
                                        {Object.values(theme).map((item, index) =>
                                            <MenuItem key={index} value={item.id}>
                                                {intl.formatMessage({id: `app.colors_${item.name}`})}
                                            </MenuItem>
                                        )}
                                    </Select>
                                </Control>
                            </Row>
                            {this.state.chart === chartType.bar.code ?
                                <React.Fragment>
                                    <Row>
                                        <Control variant="standard">
                                            <InputLabel shrink={true}>{intl.formatMessage({id: "main.graphic_bar_width"})}</InputLabel>
                                            <Select
                                                value={this.state.size}
                                                onChange={this.handleChange}
                                                inputProps={{
                                                    name: 'size'
                                                }}
                                            >
                                                {Object.values(barSize).map((item, index) =>
                                                    <MenuItem key={index} value={item.id}>
                                                        {intl.formatMessage({id: `main.graphic_bar_${item.name}`})}
                                                    </MenuItem>
                                                )}
                                            </Select>
                                        </Control>
                                        <Control variant="standard">
                                            <InputLabel shrink={true}>{intl.formatMessage({id: "main.graphic_bar_direction"})}</InputLabel>
                                            <Select
                                                value={this.state.direction}
                                                onChange={this.handleChange}
                                                inputProps={{
                                                    name: 'direction'
                                                }}
                                            >
                                                {Object.keys(barDirection).map((item, index) =>
                                                    <MenuItem key={index} value={barDirection[item]}>
                                                        {intl.formatMessage({id: `main.graphic_bar_${item}`})}
                                                    </MenuItem>
                                                )}
                                            </Select>
                                        </Control>
                                    </Row>
                                    <FormControlLabel
                                        control={
                                            <Checkbox
                                                checked={this.state.showValues}
                                                onChange={(e) => this.setState({ showValues: e.target.checked })}
                                            />
                                        }
                                        label={intl.formatMessage({id: "main.graphic_values_show"})}
                                    />
                                </React.Fragment>
                                :
                                <React.Fragment>
                                    <Row>
                                        <Control variant="standard">
                                            <InputLabel shrink={true}>{intl.formatMessage({id: "main.graphic_pie_radius"})}</InputLabel>
                                            <Select
                                                value={this.state.radius}
                                                onChange={this.handleChange}
                                                inputProps={{
                                                    name: 'radius'
                                                }}
                                            >
                                                {Object.keys(pieInnerRadius).map((item, index) =>
                                                    <MenuItem key={index} value={pieInnerRadius[item].id}>
                                                        {intl.formatMessage({id: `main.graphic_pie_radius_${item}`})}
                                                    </MenuItem>
                                                )}
                                            </Select>
                                        </Control>
                                        <Control variant="standard">
                                            <InputLabel shrink={true}>{intl.formatMessage({id: "main.graphic_pie_legend"})}</InputLabel>
                                            <Select
                                                value={this.state.legend}
                                                onChange={this.handleChange}
                                                inputProps={{
                                                    name: 'legend'
                                                }}
                                            >
                                                {Object.keys(pieLegendPosition).map((item, index) =>
                                                    <MenuItem key={index} value={pieLegendPosition[item]}>
                                                        {intl.formatMessage({id: `main.graphic_pie_legend_${item}`})}
                                                    </MenuItem>
                                                )}
                                            </Select>
                                        </Control>
                                    </Row>
                                    <Row>
                                        <FormControlLabel
                                            control={
                                                <Checkbox
                                                    checked={!!this.state.labels}
                                                    onChange={(e) => this.setState({ labels: e.target.checked })}
                                                />
                                            }
                                            label={intl.formatMessage({id: "main.graphic_pie_show_labels"})}
                                        />
                                        <FormControlLabel
                                        control={
                                            <Checkbox
                                                checked={!!this.state.percents}
                                                onChange={(e) => this.setState({ percents: e.target.checked })}
                                            />
                                        }
                                        label={intl.formatMessage({id: "main.graphic_pie_show_percents"})}
                                    />
                                    </Row>
                                </React.Fragment>
                            }
                            <Row>
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={!!this.state.sort}
                                            onChange={(e) => this.setState({ sort: e.target.checked ? sortDataType.valueasc : 0 })}
                                        />
                                    }
                                    label={intl.formatMessage({id: "main.graphic_sort"})}
                                />
                                {!!this.state.sort &&
                                    <Control variant="standard">
                                        <InputLabel shrink={true}>{intl.formatMessage({id: "main.graphic_sort"})}</InputLabel>
                                        <Select
                                            value={this.state.sort}
                                            onChange={this.handleChange}
                                            inputProps={{
                                                name: 'sort'
                                            }}
                                        >
                                            {Object.keys(sortDataType).map((item, index) =>
                                                <MenuItem key={index} value={sortDataType[item]}>
                                                    {intl.formatMessage({id: `main.graphic_${item}`})}
                                                </MenuItem>
                                            )}
                                        </Select>
                                    </Control>
                                }
                            </Row>
                        </Container>
                        <Container active={this.state.settings === settings.data.code}>
                            <RollupDefinition
                                entity={this.state.entity}
                                field={this.state.field}
                                groupField={this.state.groupField}
                                calc={this.state.calc}
                                entities={this.props.entities}
                                onChange={this.handleChange}
                                fields={this.fields}
                                graphic
                                filters={this.state.filters}
                                changeFilters={this.changeFilters}
                                fetchLinkedOptions={this.props.fetchLinkedOptions}
                                options={this.props.options}
                                labelFields={this.labelFields}
                            />
                        </Container>
                    </React.Fragment>
                </DialogContent>
                <DialogActions>
                    <Button onClick={this.props.handleClose} color="primary">
                        {intl.formatMessage({id: "app.confirm_cancel"})}
                    </Button>
                    {this.props.cardId &&
                        <Button variant="contained"
                            onClick={this.deleteCard}
                            color="secondary"
                        >
                            {intl.formatMessage({id: "app.confirm_delete"})}
                        </Button>
                    }
                    {this.props.cardId ?
                        <Button variant="contained"
                            disabled={(!this.state.label && !this.state.name) ||
                                (!this.state.label && this.state.name.replace(/\s/g, '') === '') ||
                                (!this.fields.length && this.state.calc !== aggregatefunction.Count) ||
                                (this.labelFields && !this.labelFields.length) ||
                                !!this.state.errors.length}
                            onClick={this.updateCard}
                            color="primary"
                        >
                            {intl.formatMessage({id: "app.confirm_save"})}
                        </Button>
                        :
                        <Button variant="contained"
                            disabled={(!this.state.label && !this.state.name.length) ||
                                (!this.state.label && this.state.name.replace(/\s/g, '') === '') ||
                                (!this.fields.length && this.state.calc !== aggregatefunction.Count) ||
                                (this.labelFields && !this.labelFields.length) ||
                                !!this.state.errors.length}
                            onClick={this.createCard}
                            color="primary"
                        >
                            {intl.formatMessage({id: "app.confirm_create"})}
                        </Button>
                    }
                </DialogActions>
            </React.Fragment>
        )
    }
}

const mapStateToProps = ({linkedfield}) => ({
    options: linkedfield.linkedOptions
})

export default connect(mapStateToProps, {
    createCard,
    updateCard,
    deleteCard,
    fetchLinkedOptions
})(injectIntl(GraphicConfig))
